import { ReactNode, useEffect, useRef, useState } from "react";
import { cn } from "lib/utils";

const useCountUp = (target: number, duration = 500) => {
  const [display, setDisplay] = useState(target);
  const prevRef = useRef(target);

  useEffect(() => {
    const from = prevRef.current;
    const to = target;
    if (from === to) return;

    const start = performance.now();
    let raf: number;

    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setDisplay(Math.round(from + (to - from) * progress));
      if (progress < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevRef.current = to;
      }
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  return display;
};

type Tone = "default" | "danger" | "success";

const TONE_VALUE_CLASS: Record<Tone, string> = {
  default: "text-slate-900",
  danger: "text-red-600",
  success: "text-green-600",
};

type Props = {
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  tone?: Tone;
  className?: string;
};

const GiantStat = ({ label, value, icon, tone = "default", className }: Props) => {
  const numeric = typeof value === "number" ? value : null;
  const animated = useCountUp(numeric ?? 0);
  const displayValue = numeric != null ? animated : value;

  return (
    <div
      className={cn(
        "bg-white rounded-xl border border-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] px-6 py-5 flex flex-col gap-2",
        className
      )}
    >
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold tracking-wide text-slate-500 uppercase">{label}</p>
        {icon && <span className="text-slate-300 [&>svg]:w-4 [&>svg]:h-4">{icon}</span>}
      </div>
      <p className={cn("text-5xl leading-none font-bold tabular-nums", TONE_VALUE_CLASS[tone])}>{displayValue}</p>
    </div>
  );
};

export default GiantStat;
